import React from "react";
import { FaSchool, FaUniversity, FaDatabase, FaCode, FaPython, FaBullhorn, FaCalendar, FaTrophy } from "react-icons/fa";

const FormacionSection = ({ language = 'es' }) => {
  const translations = {
    es: {
      sectionTitle: "Formación Académica",
      coursesTitle: "Cursos Realizados",
      formaciones: [
        {
          id: 1,
          title: "Secundaria Completada",
          subtitle: "Liceo León de Greif",
          description: "Empecé con la programación y aprendí muy por encima.",
          status: "Completado",
          year: "2019",
          icon: <FaSchool className="text-teal-400" />,
        },
        {
          id: 2,
          title: "Universidad de Boyacá",
          subtitle: "Ingeniería de Sistemas",
          description:
            "He trabajado en varios proyectos de desarrollo, adquiriendo experiencia práctica en diferentes tecnologías.",
          status: "En Proceso",
          year: "2020 - Presente",
          icon: <FaUniversity className="text-teal-400" />,
        },
        {
          id: 3,
          title: "Hackathon Innovate Boyacá 2025",
          subtitle: "2do Puesto - Reto Empresarial Carvajal Laboratorios",
          description:
            "Participación destacada en el hackathon obteniendo el segundo lugar en el reto empresarial propuesto por Carvajal Laboratorios.",
          status: "Completado",
          year: "2025",
          icon: <FaTrophy className="text-teal-400" />,
        },
      ],
      cursos: [
        {
          id: 1,
          title: "Construcción de Bases de Datos con MySQL",
          platform: "SENA",
          icon: <FaDatabase className="text-teal-400" />,
        },
        {
          id: 2,
          title: "Curso Profesional de Desarrollo Web",
          platform: "Código Facilito",
          icon: <FaCode className="text-teal-400" />,
        },
        {
          id: 3,
          title: "Python",
          platform: "Santander Open Academy",
          icon: <FaPython className="text-teal-400" />,
        },
        {
          id: 4,
          title: "Marketing Digital",
          platform: "SENA",
          icon: <FaBullhorn className="text-teal-400" />,
        },
      ],
    },
    en: {
      sectionTitle: "Academic Training",
      coursesTitle: "Completed Courses",
      formaciones: [
        {
          id: 1,
          title: "High School Completed",
          subtitle: "Liceo León de Greif",
          description: "I started with programming and learned the basics.",
          status: "Completed",
          year: "2019",
          icon: <FaSchool className="text-teal-400" />,
        },
        {
          id: 2,
          title: "University of Boyacá",
          subtitle: "Systems Engineering",
          description:
            "I have worked on several development projects, gaining practical experience with various technologies.",
          status: "In Progress",
          year: "2020 - Present",
          icon: <FaUniversity className="text-teal-400" />,
        },
        {
          id: 3,
          title: "Hackathon Innovate Boyacá 2025",
          subtitle: "2nd Place - Carvajal Laboratories Business Challenge",
          description:
            "Outstanding participation in the hackathon obtaining second place in the business challenge proposed by Carvajal Laboratories.",
          status: "Completed",
          year: "2025",
          icon: <FaTrophy className="text-teal-400" />,
        },
      ],
      cursos: [
        {
          id: 1,
          title: "Database Construction with MySQL",
          platform: "SENA",
          icon: <FaDatabase className="text-teal-400" />,
        },
        {
          id: 2,
          title: "Professional Web Development Course",
          platform: "Código Facilito",
          icon: <FaCode className="text-teal-400" />,
        },
        {
          id: 3,
          title: "Python",
          platform: "Santander Open Academy",
          icon: <FaPython className="text-teal-400" />,
        },
        {
          id: 4,
          title: "Digital Marketing",
          platform: "SENA",
          icon: <FaBullhorn className="text-teal-400" />,
        },
      ],
    },
  };

  const currentTexts = translations[language] || translations.es;

  const getStatusBadge = (status) => {
    const isCompleted = status === "Completado" || status === "Completed";
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
        isCompleted 
          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      }`}>
        {status}
      </span>
    );
  };

  return (
    <section className="bg-black py-16 px-4 text-white min-h-screen flex items-center">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-jersey text-4xl md:text-6xl text-white mb-4">
            {currentTexts.sectionTitle}
          </h2>
          <div className="h-1 w-24 bg-gradient-to-r from-teal-400 to-blue-400 mx-auto rounded-full" />
        </div>

        {/* Formación Académica */}
        <div className="mb-20">
          <div className="space-y-6">
            {currentTexts.formaciones.map((formacion, index) => (
              <div
                key={formacion.id}
                className={`group relative bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-teal-500/50 transition-all duration-300 ${
                  index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'
                }`}
              >
                {/* Timeline connector */}
                <div className="absolute -left-4 top-8 w-8 h-8 bg-teal-500 rounded-full border-4 border-black z-10 flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
                
                {/* Content */}
                <div className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row md:items-center gap-6">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gray-800 rounded-xl flex items-center justify-center text-3xl group-hover:bg-gray-700 transition-colors duration-300">
                        {formacion.icon}
                      </div>
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                          <h3 className="text-xl md:text-2xl font-jersey text-white mb-2 group-hover:text-teal-400 transition-colors duration-300">
                            {formacion.title}
                          </h3>
                          <p className="text-gray-400 font-medium mb-2">{formacion.subtitle}</p>
                          <p className="text-gray-500 text-sm leading-relaxed">
                            {formacion.description}
                          </p>
                        </div>
                        
                        {/* Status and Year */}
                        <div className="flex flex-col items-start md:items-end gap-2">
                          {getStatusBadge(formacion.status)}
                          <div className="flex items-center text-gray-400 text-sm">
                            <FaCalendar className="w-3 h-3 mr-2" />
                            {formacion.year}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Hover effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            ))}
          </div>
        </div>

        {/* Cursos Realizados */}
        <div>
          <div className="text-center mb-12">
            <h2 className="font-jersey text-3xl md:text-5xl text-white mb-4">
              {currentTexts.coursesTitle}
            </h2>
            <div className="h-1 w-16 bg-gradient-to-r from-teal-400 to-blue-400 mx-auto rounded-full" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {currentTexts.cursos.map((curso) => (
              <div
                key={curso.id}
                className="group bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-teal-500/50 hover:shadow-xl hover:shadow-teal-500/10 transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center text-xl group-hover:bg-gray-700 transition-colors duration-300">
                      {curso.icon}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-lg font-jersey text-white mb-1 group-hover:text-teal-400 transition-colors duration-300">
                      {curso.title}
                    </h3>
                    <p className="text-gray-400 text-sm font-medium">{curso.platform}</p>
                  </div>
                  
                  {/* Arrow */}
                  <div className="text-gray-600 group-hover:text-teal-400 transition-colors duration-300">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-8 bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-800">
            <div className="text-center">
              <div className="text-2xl font-jersey text-teal-400 mb-1">
                {currentTexts.formaciones.length}
              </div>
              <div className="text-sm text-gray-400">
                {language === 'es' ? 'Formaciones' : 'Education'}
              </div>
            </div>
            <div className="w-px h-12 bg-gray-700" />
            <div className="text-center">
              <div className="text-2xl font-jersey text-blue-400 mb-1">
                {currentTexts.cursos.length}
              </div>
              <div className="text-sm text-gray-400">
                {language === 'es' ? 'Cursos' : 'Courses'}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Timeline line */}
      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-0.5 h-32 bg-gradient-to-b from-teal-500 to-blue-500 rounded-full hidden md:block" />
    </section>
  );
};

export default FormacionSection;